import React from "react";
import { Link } from "react-router-dom";

const EDGE_CLASS = {
  live: "bg-amber-500",
  exact: "bg-green-600",
  result: "bg-amber-400",
  miss: "bg-gray-300",
};

const SHORT_NAMES = {
  "wolverhampton wanderers": "Wolves",
  "brighton & hove albion": "Brighton",
  "brighton and hove albion": "Brighton",
  "nottingham forest": "Nott'm Forest",
  "manchester city": "Man City",
  "manchester united": "Man United",
  "tottenham hotspur": "Spurs",
  "newcastle united": "Newcastle",
  "west ham united": "West Ham",
  "crystal palace": "Palace",
  "borussia monchengladbach": "Gladbach",
  "borussia mönchengladbach": "Gladbach",
  "atletico madrid": "Atlético",
  "atlético madrid": "Atlético",
};

export function shortTeamName(name) {
  const n = (name || "").trim();
  return SHORT_NAMES[n.toLowerCase()] || n;
}

function initials(name) {
  const words = (name || "").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
  if (!words[0]) return "?";
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function Crest({ name, logo, size = "md" }) {
  const box = size === "sm" ? "w-6 h-6" : "w-8 h-8 md:w-9 md:h-9";
  if (logo) {
    return <img src={logo} alt="" className={`${box} object-contain shrink-0`} loading="lazy" />;
  }
  return (
    <span
      aria-hidden
      className={`${box} shrink-0 rounded-full bg-gray-100 text-[10px] font-bold text-muted flex items-center justify-center`}
    >
      {initials(name)}
    </span>
  );
}

export function teamPageHref(competition, name) {
  if (!competition || !name) return null;
  return `/team?competition=${encodeURIComponent(competition)}&name=${encodeURIComponent(name)}`;
}

function TeamName({ name, href }) {
  const label = (
    <>
      <span className="md:hidden">{shortTeamName(name)}</span>
      <span className="hidden md:inline">{name}</span>
    </>
  );
  const className = "font-semibold text-ink text-[15px] leading-5 md:text-team line-clamp-2 md:line-clamp-1";
  if (!href) {
    return (
      <p className={className} title={name}>
        {label}
      </p>
    );
  }
  return (
    <Link
      to={href}
      title={name}
      aria-label={name}
      className={`${className} pointer-events-auto relative z-20 hover:underline`}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  );
}

function Team({ name, logo, side, extra, href }) {
  const isHome = side === "home";
  return (
    <div
      className={`min-w-0 flex flex-col items-center gap-1.5 md:gap-3 ${
        isHome ? "md:flex-row-reverse" : "md:flex-row"
      }`}
    >
      <Crest name={name} logo={logo} />
      <div className={`min-w-0 w-full text-center ${isHome ? "md:text-right" : "md:text-left"}`}>
        <TeamName name={name} href={href} />
        {extra && (
          <div className={`mt-1 flex justify-center ${isHome ? "md:justify-end" : "md:justify-start"}`}>
            {extra}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One match, drawn the same way on Home, Predict and Fixtures.
 * `center` holds the score, kickoff or score controls; `footer` holds extra controls.
 */
export default function MatchRow({
  home,
  away,
  homeExtra,
  awayExtra,
  meta,
  metaRight,
  center,
  footer,
  tone,
  to,
  competition,
  live = false,
  className = "",
  id,
}) {
  const homeHref = teamPageHref(competition, home?.name);
  const awayHref = teamPageHref(competition, away?.name);
  const body = (
    <>
      {tone && EDGE_CLASS[tone] && (
        <span aria-hidden className={`absolute left-0 inset-y-0 w-[3px] ${EDGE_CLASS[tone]}`} />
      )}
      <div className="px-3 py-3 md:px-4">
        {(meta || metaRight) && (
          <div className="flex items-center justify-between gap-2 mb-2.5 text-meta text-muted min-h-[24px]">
            <div className="min-w-0 truncate">{meta}</div>
            {metaRight && <div className="shrink-0">{metaRight}</div>}
          </div>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 md:gap-4">
          <Team side="home" name={home?.name} logo={home?.logo} extra={homeExtra} href={homeHref} />
          <div className="flex flex-col items-center justify-center min-w-[88px]">{center}</div>
          <Team side="away" name={away?.name} logo={away?.logo} extra={awayExtra} href={awayHref} />
        </div>
        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </>
  );

  const shell = `relative block overflow-hidden rounded-xl border border-line bg-white shadow-sm ${className}`;

  const liveProps = live ? { "aria-live": "polite" } : {};

  if (to) {
    return (
      <article id={id} className={`${shell} hover:border-gray-300 transition-colors`} {...liveProps}>
        <Link to={to} aria-label={`${home?.name || "Home"} versus ${away?.name || "Away"}`} className="absolute inset-0 z-0" />
        <div className="relative z-10 pointer-events-none">{body}</div>
      </article>
    );
  }
  return (
    <article id={id} className={shell} {...liveProps}>
      {body}
    </article>
  );
}

/** Points pill: green exact (5), amber right result (2–3), gray otherwise. */
export function PointsPill({ points }) {
  if (points === null || points === undefined || points === "") return null;
  const n = Number(points) || 0;
  const cls =
    n >= 5
      ? "bg-green-50 text-green-800 border-green-200"
      : n >= 2
        ? "bg-amber-50 text-amber-900 border-amber-200"
        : "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-meta font-semibold tabular-nums ${cls}`}>
      +{n} pts
    </span>
  );
}

export function pointsTone(points) {
  if (points === null || points === undefined || points === "") return undefined;
  const n = Number(points) || 0;
  if (n >= 5) return "exact";
  if (n >= 2) return "result";
  return "miss";
}
